/** One audit event from `GET /promotions/{id}/audit` — the running change history. */
export interface PromotionAuditEvent {
  id: string
  actor_name: string | null
  event_type: string // created | updated | stage_changed | deleted | imported
  change_channel: string // ui | import | system
  changes: PromotionAuditChange[] | null
  summary: string
  created_at: string
}

export interface PromotionAuditChange {
  field: string
  from: unknown
  to: unknown
}

const FIELD_LABELS: Record<string, string> = {
  start_date: "Start date",
  end_date: "End date",
  discount_amount: "Discount amount",
  discount_percentage: "Discount %",
  multibuy_quantity: "Multibuy quantity",
  multibuy_price: "Multibuy price",
  promotion_type: "Promotion type",
  planned_amount: "Planned amount",
  remote_promo_id: "Promotion ID",
  planning_group: "Planning group",
  account_group: "Account group",
  fiscal_year: "Fiscal year",
}

export function format_field_label(field: string): string {
  const known = FIELD_LABELS[field]
  if (known) return known
  const spaced = field.replace(/_/g, " ")
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/** Render a before/after value compactly: nulls become an em dash, arrays join, numbers echo. */
export function format_change_value(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—"
  if (Array.isArray(value)) return value.length === 0 ? "—" : value.map(item => format_change_value(item)).join(", ")
  if (typeof value === "object") return JSON.stringify(value)
  return String(value)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("format_field_label", () => {
    it("uses friendly labels for known fields and title-cases the rest", () => {
      expect(format_field_label("planned_amount")).toBe("Planned amount")
      expect(format_field_label("customer")).toBe("Customer")
      expect(format_field_label("some_new_field")).toBe("Some new field")
    })
  })

  describe("format_change_value", () => {
    it("renders empties as an em dash", () => {
      expect(format_change_value(null)).toBe("—")
      expect(format_change_value("")).toBe("—")
      expect(format_change_value([])).toBe("—")
    })

    it("joins arrays and echoes scalars", () => {
      expect(format_change_value(["Marinara", "Alfredo"])).toBe("Marinara, Alfredo")
      expect(format_change_value(2.4)).toBe("2.4")
      expect(format_change_value("Wake Fern")).toBe("Wake Fern")
    })
  })
}
